import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, Edit3, X, Check, Percent, Euro, Calendar, TrendingUp } from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; } };

export default function Kampagnen() {
  const [codes, setCodes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/discount-codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (c) => {
    if (!window.confirm(`Code „${c.code}" löschen?`)) return;
    try { await api.delete(`/admin/discount-codes/${c.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="kampagnen-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <TrendingUp size={22} className="text-[#D4AF37]" />
          <h1 className="font-heading text-3xl">Kampagnen · Rabatt-Codes</h1>
        </div>
        <button onClick={() => setEditing({})} data-testid="kampagnen-add-btn"
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black py-2 px-5 rounded text-[11px] uppercase tracking-widest">
          <Plus size={14} /> Neuer Code
        </button>
      </div>

      <div className="space-y-2">
        {codes.map((c) => {
          const expired = c.valid_until && c.valid_until < new Date().toISOString().slice(0, 10);
          const usedUp = c.max_uses > 0 && c.uses_count >= c.max_uses;
          return (
            <div key={c.id} className="card-lux p-4 flex items-center gap-4" data-testid={`code-row-${c.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-lg text-[#D4AF37] tracking-widest">{c.code}</span>
                  {!c.active && <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#575148] text-[#575148]">inaktiv</span>}
                  {expired && <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#7A1921] text-[#EE9CA1]">abgelaufen</span>}
                  {usedUp && <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#7A1921] text-[#EE9CA1]">aufgebraucht</span>}
                </div>
                <div className="text-sm text-[#F5F0E6]">
                  {c.discount_percent ? `${c.discount_percent}% Rabatt` : `${c.discount_amount} € Rabatt`}
                  {c.description && <span className="text-[#A8A398]"> · {c.description}</span>}
                </div>
                <div className="text-[10px] text-[#575148] mt-1 flex gap-3">
                  {c.valid_until && <span><Calendar size={9} className="inline mr-1" />bis {fmtDate(c.valid_until)}</span>}
                  <span>{c.uses_count}/{c.max_uses || "∞"} genutzt</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(c)} data-testid={`code-edit-${c.id}`} className="p-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37]"><Edit3 size={13} /></button>
                <button onClick={() => del(c)} data-testid={`code-delete-${c.id}`} className="p-2 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={13} /></button>
              </div>
            </div>
          );
        })}
        {!codes.length && <div className="text-center py-10 text-[#575148]">Noch keine Codes.</div>}
      </div>

      {editing && <CodeEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CodeEditor({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial.id ? initial : {
    code: "", discount_percent: 10, discount_amount: null,
    valid_from: "", valid_until: "", max_uses: 0, active: true, description: "",
  });
  const [mode, setMode] = useState(f.discount_percent ? "percent" : "amount");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.code.trim()) return toast.error("Code eingeben");
    const payload = {
      code: f.code.trim().toUpperCase(),
      discount_percent: mode === "percent" ? Number(f.discount_percent) : null,
      discount_amount: mode === "amount" ? Number(f.discount_amount) : null,
      valid_from: f.valid_from || null,
      valid_until: f.valid_until || null,
      max_uses: Number(f.max_uses) || 0,
      active: !!f.active,
      description: f.description,
    };
    setBusy(true);
    try {
      if (initial.id) await api.patch(`/admin/discount-codes/${initial.id}`, payload);
      else await api.post("/admin/discount-codes", payload);
      toast.success("Gespeichert");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="code-editor">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-5">
          <h2 className="font-heading text-2xl text-[#D4AF37]">{initial.id ? "Code bearbeiten" : "Neuer Rabatt-Code"}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Code</label>
            <input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} disabled={!!initial.id}
              data-testid="code-input"
              placeholder="z.B. FRUEHLING25"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono uppercase tracking-widest focus:border-[#D4AF37] outline-none disabled:opacity-50" />
          </div>
          <div className="flex gap-1 rounded border border-[#2A2A2A] p-1 text-[10px] uppercase tracking-widest">
            <button onClick={() => setMode("percent")} data-testid="mode-percent"
              className={`flex-1 py-2 rounded ${mode === "percent" ? "bg-[#D4AF37] text-black" : "text-[#A8A398]"}`}>
              <Percent size={12} className="inline mr-1" /> Prozent
            </button>
            <button onClick={() => setMode("amount")} data-testid="mode-amount"
              className={`flex-1 py-2 rounded ${mode === "amount" ? "bg-[#D4AF37] text-black" : "text-[#A8A398]"}`}>
              <Euro size={12} className="inline mr-1" /> Fester Betrag
            </button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{mode === "percent" ? "Rabatt in %" : "Rabatt in €"}</label>
            <input type="number" min={0} max={mode === "percent" ? 90 : 500}
              value={mode === "percent" ? (f.discount_percent || "") : (f.discount_amount || "")}
              onChange={(e) => setF({ ...f, [mode === "percent" ? "discount_percent" : "discount_amount"]: e.target.value })}
              data-testid="discount-value"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Gültig von</label>
              <input type="date" value={f.valid_from || ""} onChange={(e) => setF({ ...f, valid_from: e.target.value })}
                data-testid="code-from"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Gültig bis</label>
              <input type="date" value={f.valid_until || ""} onChange={(e) => setF({ ...f, valid_until: e.target.value })}
                data-testid="code-until"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Max. Einlösungen (0 = ∞)</label>
              <input type="number" min={0} value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })}
                data-testid="code-max-uses"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Status</label>
              <label className="mt-3 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="code-active" className="accent-[#D4AF37]" />
                <span className="text-sm">aktiv</span>
              </label>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Beschreibung / Zweck</label>
            <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
              placeholder="z.B. Frühlingsaktion, Newsletter-Bonus"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none text-sm" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="py-2 px-4 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest">Abbrechen</button>
          <button onClick={save} disabled={busy} data-testid="code-save"
            className="py-2 px-6 bg-[#D4AF37] text-black rounded text-[11px] uppercase tracking-widest disabled:opacity-50">
            {busy ? "…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
